import { createModel } from '@rematch/core';
import produce from 'immer';

import {
    decodeMethodResult,
    getFactoryContract,
    getMiningOneSideBoostContract,
    getMiningOneSideBoostV2Contract,
    getMiningOneSideBoostVeiZiContract,
    getPositionPoolContract,
    getVeiZiContract,
} from '../../../../../utils/contractHelpers';
import {
    getABAmountInUniswap,
    getWorthOfToken,
    getStakedPositionPriceRange,
    findPoolEntryByPoolKey,
    getPoolAPR,
    getPriceAByBDecimal,
    getPriceAByB,
    getStakedPositionPriceRangeDecimal,
    addIZIBoostAPR,
    getPriceOracleAByB,
    getPriceOracleAByBDecimal,
    getTokenStatus,
    veiZiBoostAPR,
} from './funcs';
import { isAddress } from 'ethers/lib/utils';

import {
    getMiningOneSideBoostMetaInfo,
    MiningOneSideMetaInfo,
} from './miningOneSideContractHelper';

import { RootModel } from '../../..';

import {
    FarmOneSideState,
    FarmControl,
    InitPoolListDataParams,
    InitPoolListMetaParams,
    InitPositionParams,

    PoolEntryState,
    MiningPoolData,
    MiningPoolMeta,
    PositionDetail,
    PositionEntry,
    TokenStatus,
    RefreshPoolListDataAndPositionParams,
} from './types';

import { Slot0Response } from '../../../../../types/abis/UniswapV3/UniswapV3Pool';
import { getPositionPoolKey } from '../../../common/positionPoolHelper';

import { parallelCollect } from '../../../../../net/contractCall/parallel';
import { MiningPoolUserData } from './types';

import BigNumber from 'bignumber.js';
import { tokenAddr2Token, tokenSymbol2token } from '../../../../../config/tokens';
import { amount2Decimal } from '../../../../../utils/tokenMath';
import { ChainId, FarmOneSideContractVersion, TokenSymbol } from '../../../../../types/mod';
import { getChain } from '../../../../../config/chains';
import { VEIZI_ADDRESS } from '../../../../../config/veizi/veiziContracts';
import { toContractFeeNumber } from '../../../../../utils/funcs';
import { getPositionManagerContract } from '../../../../../net/contractCall/positionManager';


export const farmOneSide = createModel<RootModel>()({
    state: {
        positionManagerContract: undefined,
        poolEntryList: [],
        farmView: {
            isFarmDataLoading: false,
            isUserDataLoading: false,
        },
        farmControl: {
            sortBy: undefined,
            stakedOnly: false,
            searchKey: undefined,
            type: 'live',
        }
    } as FarmOneSideState,
    reducers: {
        setFarmState: (state: FarmOneSideState, payload: FarmOneSideState) => {
            return { ...state, ...payload };
        },
        setFarmControl: (state: FarmOneSideState, farmControl: FarmControl) => produce(state, draft => {
            draft.farmControl = { ...farmControl };
        }),
        setPoolEntryList: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            // void freeze object when initPoolListByMeta set meta state first
            draft.poolEntryList = JSON.parse(JSON.stringify(payload));
        }),
        setPoolEntryListMeta: (state: FarmOneSideState, { chainId, poolEntryList }: { chainId: ChainId, poolEntryList: PoolEntryState[] }) => {
            return { ...state, poolEntryList, currentFarmChainId: chainId };
        },
        setPoolEntryListData: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            for (const poolEntry of payload) {
                const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, poolEntry.meta.positionPoolKey);
                draftPoolEntry.data = poolEntry.data;
            }
        }),
        setPositionData: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            const positionPoolKeySet = new Set();
            for (const poolEntry of payload) {
                const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, poolEntry.meta.positionPoolKey);
                draftPoolEntry.userData = poolEntry.userData;
                draftPoolEntry.positionList = poolEntry.positionList;
                draftPoolEntry.stakedPositionList = poolEntry.stakedPositionList;

                positionPoolKeySet.add(poolEntry.meta.positionPoolKey);
            }
            // clean other
            draft.poolEntryList.filter(p => !positionPoolKeySet.has(p.meta.positionPoolKey)).forEach(p => {
                if (p.meta.twoRewards) {
                    p.userData.earned = ['0', '0'];
                } else {
                    p.userData.earned = ['0'];
                }
                p.positionList = [];
                p.stakedPositionList = [];
            });
        }),
        setFarmDataLoading: (state: FarmOneSideState, isLoading: boolean) => produce(state, draft => {
            draft.farmView.isFarmDataLoading = isLoading;
        }),
        setUserDataLoading: (state: FarmOneSideState, isLoading: boolean) => produce(state, draft => {
            draft.farmView.isUserDataLoading = isLoading;
        }),
        togglePoolMetaInitialToggle: (state: FarmOneSideState, positionPoolKey: string) => produce(state, draft => {
            const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, positionPoolKey);
            draftPoolEntry.meta.initialToggle = !draftPoolEntry.meta.initialToggle;
        }),
    },
    effects: (dispatch) => ({
        async initPoolListMeta(initPoolListMetaParams: InitPoolListMetaParams): Promise<void> {
            const { chainId, metaList } = initPoolListMetaParams;
            if (!chainId) { return; }
            const startTime = new Date();

            const poolEntryList = [] as PoolEntryState[];
            // TODO filter
            // TODO contract multicall or web3 batch request or parallel
            for (const configMeta of metaList ?? []) {
                const data = {} as MiningPoolData;
                const contractVersion = (configMeta.contractVersion) ? configMeta.contractVersion : FarmOneSideContractVersion.V2;
                const additionalKey = configMeta.additionalKey ?? '01';

                const positionPoolKey = getPositionPoolKey(configMeta.tokenA.address, configMeta.tokenB.address, configMeta.feeTier, contractVersion, additionalKey);

                const meta = { ...configMeta, positionPoolKey, contractVersion } as MiningPoolMeta;

                const userData = {} as MiningPoolUserData;
                if (meta.twoRewards) {
                    userData.earned = ['0', '0'];
                } else {
                    userData.earned = ['0'];
                }
                const poolEntryState = { meta, data, userData } as PoolEntryState;
                poolEntryList.push(poolEntryState);
            }
            dispatch.farmOneSide.setPoolEntryListMeta({ chainId, poolEntryList });
            // sync init meta data for render basic view
            console.log(`initPoolListMeta end, ${(new Date()).getTime() - startTime.getTime()} ms`);
        },
        async initPoolListData(initPoolListDataParams: InitPoolListDataParams, rootState): Promise<void> {
            const { chainId, web3, positionFactoryContract } = initPoolListDataParams;
            if (!chainId || !web3 || !positionFactoryContract) { return; }
            if (rootState.farmOneSide.poolEntryList.length === 0) { return; }
            /// check data consistency
            const positionFactoryContractExpected = getFactoryContract(chainId, web3);
            if (
                !positionFactoryContractExpected ||
                positionFactoryContractExpected.options.address !== positionFactoryContract.options.address
            ) { return; }

            const blockDeltaU = getChain(chainId)?.blockDeltaU ?? 10;

            const poolEntryDataList = [] as PoolEntryState[];
            const asyncProcessList: Promise<void>[] = [];
            for (const poolEntry of rootState.farmOneSide.poolEntryList) {
                const { meta } = poolEntry;
                const data = {} as MiningPoolData;
                const poolEntryData = {
                    meta: { positionPoolKey: meta.positionPoolKey } as MiningPoolMeta,
                    data
                } as PoolEntryState;

                poolEntryDataList.push(poolEntryData);

                const asyncProcess = async () => {
                    // 1. get mining contract info
                    const miningOnesideMetaInfo: MiningOneSideMetaInfo = await getMiningOneSideBoostMetaInfo(meta.miningContract, web3, meta.contractVersion);

                    // 2. get position pool slot0 info from pool factory contract
                    const positionPoolAddress = await positionFactoryContract.methods.getPool(meta.tokenA.address, meta.tokenB.address, toContractFeeNumber(meta.feeTier)).call();
                    const poolContract = getPositionPoolContract(positionPoolAddress, web3);
                    const slot0: Slot0Response = await poolContract?.methods.slot0().call();

                    // 3. get swap and reward token price and set price data
                    const tokenPriceAB = await parallelCollect(
                        dispatch.token.fetchTokenPriceIfMissing(meta.tokenA),
                        dispatch.token.fetchTokenPriceIfMissing(meta.tokenB),
                        dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId))
                    );
                    const tokenPriceB: number = tokenPriceAB[1];
                    const tokenPriceA: number = tokenPriceAB[0];
                    const tokenPriceiZi: number = tokenPriceAB[2];

                    data.tokenPriceIZIDecimal = await dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId));

                    data.isEnded = Number(miningOnesideMetaInfo.miningContractInfo.endBlock_) < Number(rootState.block.currentBlock);
                    data.endBlock = Number(miningOnesideMetaInfo.miningContractInfo.endBlock_);
                    data.secondsLeft = data.isEnded ? 0 : (Number(miningOnesideMetaInfo.miningContractInfo.endBlock_) - Number(rootState.block.currentBlock)) * blockDeltaU;

                    if (meta.tokenA.symbol === meta.tokenUni.symbol) {
                        data.tokenPriceUniDecimal = tokenPriceA;
                        data.tokenPriceLockDecimal = tokenPriceB;
                    } else {
                        data.tokenPriceUniDecimal = tokenPriceB;
                        data.tokenPriceLockDecimal = tokenPriceA;
                    }

                    data.tokenPriceADecimal = tokenPriceA;
                    data.tokenPriceBDecimal = tokenPriceB;

                    data.vLiquidity = Number(miningOnesideMetaInfo.miningContractInfo.totalVLiquidity_) * 1e6;
                    data.lockBoostMultiplier = Number(miningOnesideMetaInfo.miningContractInfo.lockBoostMultiplier_);
                    data.totalNIZI = Number(miningOnesideMetaInfo.miningContractInfo.totalNIZI_ ?? 0);
                    data.totalValidVeiZi = Number(miningOnesideMetaInfo.miningContractInfo.totalValidVeiZi_ ?? 0);

                    data.vLiquidityWorth = (meta.tokenA.symbol === meta.tokenLock.symbol) ?
                        getWorthOfToken(meta.tokenB, data.vLiquidity, tokenPriceB)
                        :
                        getWorthOfToken(meta.tokenA, data.vLiquidity, tokenPriceA);
                    data.tokenUniWorth = data.vLiquidityWorth / data.lockBoostMultiplier;
                    data.capital = data.tokenUniWorth + data.vLiquidityWorth;
                    data.tvl = data.capital + getWorthOfToken(
                        tokenSymbol2token(TokenSymbol.IZI, chainId),
                        data.totalNIZI,
                        tokenPriceiZi,
                    );

                    let apr = 0;
                    data.reward = [];
                    data.rewardTokens = [];
                    data.rewardTokenPrice = [];
                    for (const rewardInfo of miningOnesideMetaInfo.rewardInfos) {
                        const rewardToken = tokenAddr2Token(rewardInfo.rewardToken, chainId);

                        const rewardTokenPrice = await dispatch.token.fetchTokenPriceIfMissing(rewardToken) ?? 0;
                        if (data.vLiquidityWorth > 0) {
                            apr += getPoolAPR(rewardInfo.rewardPerBlock, chainId, data.capital, { left: rewardToken, right: rewardTokenPrice });
                        }
                        const rewardDecimal = (amount2Decimal(
                            new BigNumber(Number(rewardInfo.rewardPerBlock)),
                            rewardToken
                        ) ?? 0);
                        data.reward.push([rewardToken, String(rewardDecimal)]);
                        data.rewardTokens.push(rewardToken);
                        data.rewardTokenPrice.push(rewardTokenPrice);
                    }

                    if (meta.iZiBoost || meta.veiZiBoost) {
                        data.apr = [apr / 2.5, apr];
                    } else {
                        data.apr = [apr];
                    }

                    // 4. set position data
                    data.positionPoolContract = positionPoolAddress;
                    data.positionSqrtPriceX96 = slot0.sqrtPriceX96;
                    data.positionTick = Number(slot0.tick);
                    data.currentTick = Number(slot0.tick);
                    data.priceAByBDecimal = getPriceAByBDecimal(chainId, meta.tokenA, meta.tokenB, slot0.sqrtPriceX96);
                    data.priceAByB = getPriceAByB(meta.tokenA, meta.tokenB, slot0.sqrtPriceX96);

                    data.oraclePriceAByB = getPriceOracleAByB(miningOnesideMetaInfo.oraclePrice.avgSqrtPriceX96);
                    data.oraclePriceAByBDecimal = getPriceOracleAByBDecimal(chainId, meta.tokenA, meta.tokenB, miningOnesideMetaInfo.oraclePrice.avgSqrtPriceX96);
                    console.log('avg tick: ', miningOnesideMetaInfo.oraclePrice.avgTick);
                    data.oracleTick = Number(miningOnesideMetaInfo.oraclePrice.avgTick);
                    console.log('data oracle tick: ', data.oracleTick);

                };
                asyncProcessList.push(asyncProcess());
            }
            await Promise.all(asyncProcessList);

            dispatch.farmOneSide.setPoolEntryListData(poolEntryDataList);
            // .finally(() => dispatch.farm.setFarmDataLoading(false));
        },
        async initPoolList(initPoolListParams: InitPoolListMetaParams & InitPoolListDataParams): Promise<void> {
            await dispatch.farmOneSide.initPoolListMeta(initPoolListParams);
            await dispatch.farmOneSide.initPoolListData(initPoolListParams);
        },
        async initPosition(initPositionParams: InitPositionParams, rootState): Promise<void> {

            const { chainId, web3, positionManagerContract, account } = initPositionParams;

            if (!chainId || !web3 || !account || !positionManagerContract || !rootState.farmOneSide.poolEntryList) { return; }
            if (rootState.farmOneSide.poolEntryList.length === 0) { return; }

              /// check data consistency
              const positionManagerContractExpected = getPositionManagerContract(chainId, web3);
              if (
                !positionManagerContractExpected ||
                positionManagerContractExpected.options.address !== positionManagerContract.options.address
            ) { return; }

            // dispatch.farm.setUserDataLoading(true);
            // 1. get total nft by account
            const unstakedTokenTotal = await positionManagerContract.methods.balanceOf(account).call().then((unstakedTokenTotal: number) => unstakedTokenTotal);

            // 2. get unstaked tokenId list by unstakedTokenTotal
            const unstakedTokenIdMulticallData = [];
            for (let i = 0; i < unstakedTokenTotal; i++) {
                unstakedTokenIdMulticallData.push(positionManagerContract.methods.tokenOfOwnerByIndex(account, i).encodeABI());
            }

            const stakedTokenIdList: string[] = [];
            const stakedTokenStatusList: TokenStatus[] = [];

            type miningPoolValueType = { tokenIdSet: Set<string>, address: string, poolEntry: PoolEntryState }
            const miningPoolData = {} as { [index: string]: miningPoolValueType };
            const asyncProcessList: Promise<void>[] = [];

            const veiZiAddress = VEIZI_ADDRESS[chainId];
            const veiZiContract = getVeiZiContract(veiZiAddress, web3);
            const stakingInfo = await veiZiContract?.methods.stakingInfo(account).call();

            const veiZi = Number(stakingInfo?.amount ?? 0);
            const veiZiNftId = stakingInfo?.nftId ?? '0';

            const veiZiDecimal = amount2Decimal(new BigNumber(stakingInfo?.amount ?? 0), tokenSymbol2token(TokenSymbol.IZI, chainId)) ?? 0;

            // get staked tokenIds and corresponding tokenStatus

            for (const poolEntryState of rootState.farmOneSide.poolEntryList) {
                const asyncProcess = async () => {
                    const miningContract = getMiningOneSideBoostContract(poolEntryState.meta.miningContract, web3, poolEntryState.meta.contractVersion);

                    const positionPoolKey = poolEntryState.meta.positionPoolKey;

                    // const { left: poolEarnedList, right: tokenIds } = await parallelCollectPair(
                    //     miningContract?.methods.pendingRewards(account).call() as Promise<string[]>,
                    //     miningContract?.methods.getTokenIds(account).call() as Promise<string[]>
                    // );

                    const poolEarnedList = await miningContract?.methods.pendingRewards(account).call();
                    const tokenIds = await miningContract?.methods.getTokenIds(account).call();

                    const userData = {} as MiningPoolUserData;
                    userData.earned = poolEarnedList;
                    userData.veiZi = veiZi;
                    userData.veiZiNftId = veiZiNftId;
                    userData.veiZiDecimal = veiZiDecimal;
                    userData.vLiquidity = 0;
                    userData.capital = 0;

                    const twoRewards = poolEntryState.meta.twoRewards;
                    const contractVersion = poolEntryState.meta.contractVersion;
                    const iZiBoost = poolEntryState.meta.iZiBoost;

                    const poolEntry = {
                        meta: { positionPoolKey, twoRewards, contractVersion, iZiBoost } as MiningPoolMeta,
                        userData: userData,
                        positionList: [] as PositionEntry[],
                        stakedPositionList: [] as PositionEntry[],
                    } as PoolEntryState;

                    const tokenStatus: TokenStatus[] = await getTokenStatus(miningContract, tokenIds);

                    miningPoolData[poolEntryState.meta.miningContract] = { tokenIdSet: new Set(tokenIds), address: poolEntryState.meta.miningContract, poolEntry };

                    stakedTokenIdList.push(...tokenIds);
                    stakedTokenStatusList.push(...tokenStatus);
                };
                asyncProcessList.push(asyncProcess());
            }

            await Promise.all(asyncProcessList);

            // we do not consider unstaked token now

            const allTokenId = [...stakedTokenIdList];
            const allTokenStatus = [...stakedTokenStatusList];
            const positionMulticallData = allTokenId.map(tokId => positionManagerContract.methods.positions(tokId).encodeABI());
            const positionResult: string[] = await positionManagerContract.methods.multicall(
                positionMulticallData
            ).call().then((positions: string[]) => positions);
            const positions: PositionDetail[] = positionResult.map((p, i) => {
                const position: PositionDetail = decodeMethodResult(positionManagerContract, 'positions', p);
                position.tokenId = allTokenId[i];
                position.tokenStatus = allTokenStatus[i];
                return position;
            });

            const stakedTokenIdSet = new Set(stakedTokenIdList);
            const stakedPositionList = positions.filter(p => stakedTokenIdSet.has(p.tokenId));

            // 7. get pendingReward for each stakedPosition
            const asyncRewardProcessList: Promise<void>[] = [];
            for (const stakedPosition of stakedPositionList) {
                const asyncRewardProcess = async () => {
                    const stakedTokenId = stakedPosition.tokenId;
                    const miningPool = Object.values(miningPoolData).filter(d => d.tokenIdSet.has(stakedTokenId))[0];
                    if (miningPool.poolEntry.meta.contractVersion === FarmOneSideContractVersion.V2) {
                        const miningContract = getMiningOneSideBoostV2Contract(miningPool.address, web3);
                        stakedPosition.earned = await miningContract?.methods.pendingReward(stakedTokenId).call();
                    }
                };
                asyncRewardProcessList.push(asyncRewardProcess());
            }
            await Promise.all(asyncRewardProcessList);

            // 8. pure function calculate data
            for (const position of positions) {

                const isStaked = (position.tokenStatus !== undefined);

                if (!isStaked) {
                    continue;
                }

                // let miningPool: miningPoolValueType;
                const miningPool = Object.values(miningPoolData).find(d => d.tokenIdSet.has(position.tokenId.toString())) as miningPoolValueType;

                const { meta, data } = rootState.farmOneSide.poolEntryList.find(p => p.meta.miningContract === miningPool.address) as PoolEntryState;
                const poolEntry = miningPool.poolEntry;
                const [tokenPriceA, tokenPriceB, tokenPriceiZi] = await parallelCollect(
                    dispatch.token.fetchTokenPriceIfMissing(meta.tokenA),
                    dispatch.token.fetchTokenPriceIfMissing(meta.tokenB),
                    dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId)),
                );
                // let amountLock = 0;
                const positionEntry = { nftId: position.tokenId, isStaked } as PositionEntry;

                positionEntry.vLiquidity = Number(position.tokenStatus.vLiquidity) * 1e6;
                const amountLock = new BigNumber(position.tokenStatus.lockAmount);
                positionEntry.amountLockDecimal = Number(amount2Decimal(amountLock, meta.tokenLock));


                positionEntry.vLiquidityWorth = getWorthOfToken(
                    (meta.tokenLock.symbol === meta.tokenA.symbol ? meta.tokenB : meta.tokenA),
                    positionEntry.vLiquidity,
                    (meta.tokenLock.symbol === meta.tokenA.symbol ? tokenPriceB : tokenPriceA),
                );

                positionEntry.amountUniWorth = positionEntry.vLiquidityWorth / data.lockBoostMultiplier;

                positionEntry.capital = positionEntry.amountUniWorth + positionEntry.vLiquidityWorth;
                positionEntry.tvl = positionEntry.capital + getWorthOfToken(
                    tokenSymbol2token(TokenSymbol.IZI, chainId),
                    Number(position.tokenStatus.nIZI ?? 0),
                    tokenPriceiZi,
                );

                positionEntry.liquidity = position.liquidity;
                positionEntry.tickLower = position.tickLower;
                positionEntry.tickUpper = position.tickUpper;

                const lockPriceDecimal = (meta.tokenLock.symbol === meta.tokenA.symbol ? tokenPriceA : tokenPriceB);
                positionEntry.amountLockWorth = lockPriceDecimal * positionEntry.amountLockDecimal;

                const [priceLower, priceUpper] = getStakedPositionPriceRange(position, meta.tokenLock, meta.tokenUni);
                positionEntry.minPrice = priceLower;
                positionEntry.maxPrice = priceUpper;
                const [priceLowerDecimal, priceUpperDecimal] = getStakedPositionPriceRangeDecimal(meta.tokenLock, meta.tokenUni, position);
                positionEntry.minPriceDecimal = priceLowerDecimal;
                positionEntry.maxPriceDecimal = priceUpperDecimal;

                positionEntry.earnedDecimal = [];
                positionEntry.earnedWorth = [];

                positionEntry.earned = [];
                if (poolEntry.meta.contractVersion === FarmOneSideContractVersion.V2) {
                    for (const idx in data.rewardTokens) {
                        const rewardToken = data.rewardTokens[idx];
                        const earnedDecimal = amount2Decimal(new BigNumber(position.earned[idx]), rewardToken);
                        positionEntry.earned.push(Number(position.earned[idx]));
                        positionEntry.earnedDecimal.push(Number(earnedDecimal));
                        const rewardTokenPrice = data.rewardTokenPrice[idx];
                        const earnedWorth: number = getWorthOfToken(rewardToken, Number(position.earned[idx]), rewardTokenPrice);
                        positionEntry.earnedWorth.push(earnedWorth);
                    }
                }

                positionEntry.niZi = Number(position.tokenStatus.nIZI ?? 0);
                positionEntry.niZiDecimal = Number(amount2Decimal(new BigNumber(positionEntry.niZi), tokenSymbol2token(TokenSymbol.IZI, chainId)));

                if (positionEntry.vLiquidity > 0) {
                    if (meta.contractVersion === FarmOneSideContractVersion.V2) {
                        positionEntry.apr = addIZIBoostAPR({ data } as PoolEntryState, chainId, positionEntry.vLiquidity, positionEntry.capital, positionEntry.niZi, 0);
                    } else {
                        positionEntry.apr = 0;
                    }
                } else {
                    positionEntry.apr = 0;
                }

                const [amountAInUniswap, amountBInUniswap] = getABAmountInUniswap(position, data.positionTick);
                positionEntry.amountAInUniswapDecimal = Number(amount2Decimal(new BigNumber(amountAInUniswap), meta.tokenA));
                positionEntry.amountBInUniswapDecimal = Number(amount2Decimal(new BigNumber(amountBInUniswap), meta.tokenB));
                positionEntry.worthAInUniswap = getWorthOfToken(
                    meta.tokenA,
                    amountAInUniswap,
                    tokenPriceA,
                );
                positionEntry.worthBInUniswap = getWorthOfToken(
                    meta.tokenB,
                    amountBInUniswap,
                    tokenPriceB,
                );


                // const [fee0InUniswap, fee1InUniswap] = get
                poolEntry.stakedPositionList.push(positionEntry);
                poolEntry.userData.capital += positionEntry.capital;
                poolEntry.userData.vLiquidity += positionEntry.vLiquidity;

            }

            // 9. miningContract is approved
            const miningContractList = Object.keys(miningPoolData);
            const isApprovedMulticallData = miningContractList.map(miningContract => positionManagerContract.methods.isApprovedForAll(account, miningContract).encodeABI());
            const isApprovedResult: boolean[] = await positionManagerContract.methods.multicall(isApprovedMulticallData).call()
                .then((isApprovedList: string[]) => isApprovedList.map(isApproved => Number(isApproved) !== 0));
            for (const i in miningContractList) {
                miningPoolData[miningContractList[i]].poolEntry.userData.isApprovedForAll = isApprovedResult[i];
                const { meta, data } = rootState.farmOneSide.poolEntryList.find(p => p.meta.miningContract === miningContractList[i]) as PoolEntryState;
                if (meta.contractVersion === FarmOneSideContractVersion.VEIZI) {
                    const miningOneSideBoostVeiZi = getMiningOneSideBoostVeiZiContract(miningContractList[i], web3);
                    const userStatus = await miningOneSideBoostVeiZi?.methods.userStatus(account).call();
                    const userData = miningPoolData[miningContractList[i]].poolEntry.userData;
                    userData.vLiquidity = Number(userStatus.vLiquidity) * 1e6;
                    userData.validVeiZi = Number(userStatus.validVeiZi);
                    userData.apr = veiZiBoostAPR({ data } as PoolEntryState, chainId, userData.vLiquidity, userData.capital, meta.veiZiBoost ? userData.veiZi : 0);
                }
            }

            // TODO save positions and positionManagerContract address
            dispatch.farmOneSide.setPositionData(Object.values(miningPoolData).map(m => m.poolEntry));
        },
        async refreshPoolListDataAndPosition(refreshPoolListDataAndPositionParams: RefreshPoolListDataAndPositionParams): Promise<void> {
            await dispatch.farmOneSide.initPoolListData(refreshPoolListDataAndPositionParams as InitPoolListDataParams);
            await dispatch.farmOneSide.initPosition(refreshPoolListDataAndPositionParams as InitPositionParams);
        },
        async cleanPositionIfExist(account: string | null | undefined, rootState): Promise<void> {
            if (!isAddress(account as string)) { return; }
            if (!rootState.farmOneSide.poolEntryList.find(p => p.positionList?.length || p.stakedPositionList?.length)) { return; }
            // clean user data
            dispatch.farmOneSide.setPositionData([]);
            console.info('cleanPosition end');
        },
    })
});
